import { useEffect, useState } from 'react';
import './style.scss';
import type { ICarInformationState } from '../CarInformation';
import type { ITravelInformationState } from '../TravelInformation';
import { PublicTransportCalculator } from '../../helpers/public-transport.calculator';
import { PublicTransportConstants } from '../../constants/public-transport.constants';
import { VariableCarCostsCalculator } from '../../helpers/variable-car-costs.calculator';
import { TotalCarCostsCalculator } from '../../helpers/total-car-cost.calculator';
import { TranslationService } from '../../../../services/translation.service';
import { SanitizerService } from '../../../../services/sanitizer.service';
import ResultTextHelper from '../../helpers/result-text-helper';
import type { ITotalCostResult, IVariableCostResult } from '../../models/result.model';
import CollapsiblePanel from '../../../Shared/CollapsiblePanel';
import { CarUser } from '../../models/car-user.enum';
import Currency from '../../../Shared/Currency';

export default function CalculationSummary({
  travelInformation,
  carInformation,
  updateResult,
  resetUpdateResult,
}: {
  travelInformation: ITravelInformationState;
  carInformation: ICarInformationState;
  updateResult: boolean;
  resetUpdateResult: () => void;
}) {
  const [calculatedCostPublicTransportState, setcalculatedCostPublicTransportState] = useState({
    costPerMonth: 0,
    ticketTranslation: '',
    selectedTicketCost: 0,
  });
  const [calculatedCarCostState, setCalculatedCarCostState] = useState<ITotalCostResult>({
    totalCost: 0,
    totalFuelCost: 0,
    insuranceCost: 0,
    loanCost: 0,
    leasingCostPerMonth: 0,
    parkingFee: 0,
    congestionTax: 0,
    vehicleTax: 0,
    depreciation: 0,
    serviceCost: 0,
    travelDeduction: 0,
    yearlyCommuteGreatherThanTotalDistance: false,
    yearlyCommuteKm: 0,
  });
  const [calculatedVariableCarCostState, setcalculatedVariableCarCostState] = useState<IVariableCostResult>({
    costPerMonth: 0,
    variableDepreciation: 0,
    fuelCost: 0,
  });

  useEffect(() => {
    function updateResultCalculations() {
      // Public transport
      if (!travelInformation.value.travelDaysPerMonth) {
        return;
      }

      const publicTransportCost = PublicTransportCalculator.calculateCost(
        {
          numberOfTravelDaysPerMonth: travelInformation.value.travelDaysPerMonth,
          zonesToTravelWithin: travelInformation.value.travelZones,
          preferredTicketType: travelInformation.value.prefferredTicketType,
        },
        PublicTransportConstants
      );

      // Calculate / get price for the selected ticket price per period.
      const { selectedTicketCost, ticketTranslation } = ResultTextHelper.getSelectedTicketAndCost(travelInformation.value.prefferredTicketType, travelInformation.value.travelZones);

      setcalculatedCostPublicTransportState({
        costPerMonth: publicTransportCost.costInSek,
        ticketTranslation,
        selectedTicketCost,
      });

      setcalculatedVariableCarCostState(VariableCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value));

      // // Fixed car costs
      setCalculatedCarCostState(TotalCarCostsCalculator.calculateCost(carInformation.value, travelInformation.value));
    }

    if (updateResult) {
      updateResultCalculations();
      resetUpdateResult();
    }
  }, [updateResult, resetUpdateResult, travelInformation, carInformation]);

  return (
    <>
      <div className="calculation-summary my-2">
        <h2 className="calculation-summary__heading">{TranslationService.get('pricecalculatorpage.summary.heading')}</h2>
        <CollapsiblePanel id="public-transport-summary" className="border-top" buttonText={TranslationService.get('pricecalculatorpage.summary.publictransport')}>
          <div className="table-responsive-sm">
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <td>{calculatedCostPublicTransportState.ticketTranslation}</td>
                  <td className="text-end">
                    <Currency amount={calculatedCostPublicTransportState.selectedTicketCost} showFractionDigits={false} />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="h4">
                  <td>{TranslationService.get('pricecalculatorpage.summary.costpermonth')}</td>
                  <td className="text-end ">
                    <Currency amount={calculatedCostPublicTransportState.costPerMonth} showFractionDigits={false} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CollapsiblePanel>
        <CollapsiblePanel id="variable-cost-summary" buttonText={TranslationService.get('pricecalculatorpage.summary.commutebycar')}>
          <div className="table-responsive-sm">
            <table className="table table-borderless">
              <tbody>
                {[
                  carInformation.value.carUser === CarUser.CarOwner && {
                    label: TranslationService.get('pricecalculatorpage.summary.variabledepreciation'),
                    amount: calculatedVariableCarCostState.variableDepreciation,
                    description: 'variable-depreciation-explanation',
                  },
                  TranslationService.get('pricecalculatorpage.summary.variabledepreciationexplanation') &&
                    carInformation.value.carUser === CarUser.CarOwner && {
                      label: TranslationService.get('pricecalculatorpage.summary.variabledepreciationexplanation'),
                      amount: null,
                      className: 'exclamation-circle',
                      id: 'variable-depreciation-explanation',
                    },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.parkingfee'),
                    amount: calculatedCarCostState.parkingFee,
                    className: '',
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.congestiontax'),
                    amount: calculatedCarCostState.congestionTax,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.fuelcost'),
                    amount: calculatedVariableCarCostState.fuelCost,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.traveldeduction'),
                    amount: calculatedCarCostState.travelDeduction > 0 ? calculatedCarCostState.travelDeduction * -1 : 0,
                    amountClassName: calculatedCarCostState.travelDeduction > 0 ? 'text-danger' : '',
                  },
                ]
                  .filter(Boolean)
                  .map((x, label) => (
                    <tr key={label} id={x && x.id ? x.id : undefined} aria-describedby={x && x.description ? x.description : undefined}>
                      <td className={x && x.className ? x.className : undefined}>{x && x.label ? x.label : undefined}</td>
                      <td className={x && x.amountClassName ? x.amountClassName : undefined}>{x && x.amount && <Currency amount={x.amount} showFractionDigits={false} />}</td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr className="h4">
                  <td>{TranslationService.get('pricecalculatorpage.summary.costpermonth')}</td>
                  <td className="">
                    <Currency amount={calculatedVariableCarCostState.costPerMonth} showFractionDigits={false} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CollapsiblePanel>
        <CollapsiblePanel id="total-cost-summary" buttonText={TranslationService.get('pricecalculatorpage.summary.totalcarcost')}>
          <div className="table-responsive-sm">
            <table className="table table-borderless">
              <tbody>
                {[
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.parkingfee'),
                    amount: calculatedCarCostState.parkingFee,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.congestiontax'),
                    amount: calculatedCarCostState.congestionTax,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.fuelcost'),
                    amount: calculatedCarCostState.totalFuelCost,
                  },
                  carInformation.value.carUser === CarUser.CarOwner && {
                    label: TranslationService.get('pricecalculatorpage.summary.depreciation'),
                    amount: calculatedCarCostState.depreciation,
                    description: 'depreciation-explanation',
                  },
                  TranslationService.get('pricecalculatorpage.summary.depreciationexplanation') &&
                    carInformation.value.carUser === CarUser.CarOwner && {
                      label: TranslationService.get('pricecalculatorpage.summary.depreciationexplanation'),
                      amount: null,
                      className: 'exclamation-circle',
                      id: 'depreciation-explanation',
                    },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.insurance'),
                    amount: calculatedCarCostState.insuranceCost,
                  },
                  carInformation.value.carUser === CarUser.CarOwner && {
                    label: TranslationService.get('pricecalculatorpage.summary.loan'),
                    amount: calculatedCarCostState.loanCost,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.vehicletax'),
                    amount: calculatedCarCostState.vehicleTax,
                  },
                  carInformation.value.carUser === CarUser.CarLeaser && {
                    label: TranslationService.get('pricecalculatorpage.summary.leasingcostpermonth'),
                    amount: calculatedCarCostState.leasingCostPerMonth,
                  },
                  carInformation.value.carUser === CarUser.CarOwner && {
                    label: TranslationService.get('pricecalculatorpage.summary.serviceandrepair'),
                    amount: calculatedCarCostState.serviceCost,
                  },
                  {
                    label: TranslationService.get('pricecalculatorpage.summary.traveldeduction'),
                    amount: calculatedCarCostState.travelDeduction > 0 ? calculatedCarCostState.travelDeduction * -1 : 0,
                    amountClassName: calculatedCarCostState.travelDeduction > 0 ? 'text-danger' : '',
                  },
                ]
                  .filter(Boolean)
                  .map((x, label) => (
                    <tr key={label} id={x && x.id ? x.id : undefined} aria-describedby={x && x.description ? x.description : undefined}>
                      <td className={x && x.className ? x.className : undefined}>{x && x.label ? x.label : undefined}</td>
                      <td className={x && x.amountClassName ? x.amountClassName : undefined}>{x && x.amount && <Currency amount={x.amount} showFractionDigits={false} />}</td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr className="h4">
                  <td>{TranslationService.get('pricecalculatorpage.summary.costpermonth')}</td>
                  <td>
                    <Currency amount={calculatedCarCostState.totalCost} showFractionDigits={false} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CollapsiblePanel>
      </div>
      <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('pricecalculatorpage.summary.calculationssupporttext')) }}></div>
    </>
  );
}
